import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { 
  UsersIcon, 
  BookOpenIcon, 
  AcademicCapIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const StatCard = ({ title, value, icon, bgColor = 'bg-blue-500', trend = null }) => (
  <div className={`p-6 rounded-2xl shadow-xl text-white ${bgColor} hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden`}>
    <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-10 rounded-full translate-y-12 -translate-x-12"></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 rounded-xl bg-white bg-opacity-20 backdrop-blur-sm">
          {React.createElement(icon, { className: "h-6 w-6" })}
        </div>
        {trend && (
          <div className={`flex items-center text-xs font-medium ${trend > 0 ? 'text-green-200' : 'text-red-200'}`}>
            {trend > 0 ? (
              <CheckCircleIcon className="w-4 h-4 mr-1" />
            ) : (
              <XCircleIcon className="w-4 h-4 mr-1" />
            )}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-1">{title}</p>
        <p className="text-3xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

const ActivityItem = ({ activity }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'enrollment':
        return <AcademicCapIcon className="w-5 h-5 text-blue-600" />;
      case 'course':
        return <BookOpenIcon className="w-5 h-5 text-green-600" />;
      case 'user':
        return <UsersIcon className="w-5 h-5 text-purple-600" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'enrollment':
        return 'bg-blue-50 border-blue-200';
      case 'course':
        return 'bg-green-50 border-green-200';
      case 'user':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <li className={`p-4 rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md ${getActivityColor(activity.type)}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          {getActivityIcon(activity.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 font-medium leading-relaxed">{activity.message}</p>
          <p className="text-xs text-gray-500 mt-2 flex items-center">
            <ClockIcon className="w-3 h-3 mr-1" />
            {new Date(activity.timestamp).toLocaleString('id-ID', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
    </li>
  );
};

const AdminDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get('/admin/dashboard-summary');
        if (response.data && response.data.status === 'success') {
          setSummary(response.data.data.summary);
        } else {
          setError('Gagal mengambil data ringkasan.');
        }
      } catch (err) {
        console.error("Error fetching dashboard summary:", err);
        setError(err.response?.data?.message || 'Terjadi kesalahan server. Coba lagi nanti.');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();

    const fetchActivities = async () => {
      try {
        setActivitiesLoading(true);
        setActivitiesError('');
        const response = await api.get('/admin/activities');
        if (response.data && response.data.status === 'success') {
          setActivities(response.data.data.activities);
        } else {
          setActivitiesError('Failed to load recent activities.');
        }
      } catch (err) {
        console.error("Error fetching recent activities:", err);
        setActivitiesError(err.response?.data?.message || 'Server error fetching activities.');
      } finally {
        setActivitiesLoading(false);
      }
    };
    fetchActivities();
  }, []);

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      const response = await api.get('/admin/users/export-excel', {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-data-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Gagal mengunduh file Excel. Silakan coba lagi.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading || (!summary && activitiesLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Summary</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!summary && !loading && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ChartBarIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Tidak Ada Data</h3>
            <p className="text-gray-600">Tidak ada data ringkasan untuk ditampilkan.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ChartBarIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Admin Dashboard</h1>
              <p className="text-lg text-gray-600">Selamat datang di panel administrasi TERAPLUS</p>
              
              {/* Download Excel Button */}
              <div className="mt-6">
                <button
                  onClick={handleDownloadExcel}
                  disabled={downloading}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {downloading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Mengunduh...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                      Download Data Excel
                    </>
                  )}
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  Unduh data pengguna dengan Nama Lengkap, Email, Affiliasi, No HP, dan skor Pretest/Posttest
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <StatCard 
              title="Total Users" 
              value={summary.totalUsers !== undefined ? summary.totalUsers : 'N/A'} 
              icon={UsersIcon}
              bgColor="bg-gradient-to-r from-sky-500 to-blue-600"
              trend={summary.userGrowth}
            />
            <StatCard 
              title="Total Courses" 
              value={summary.totalCourses !== undefined ? summary.totalCourses : 'N/A'}
              icon={BookOpenIcon}
              bgColor="bg-gradient-to-r from-emerald-500 to-green-600"
              trend={summary.courseGrowth}
            />
            <StatCard 
              title="Active Enrollments" 
              value={summary.activeEnrollments !== undefined ? summary.activeEnrollments : 'N/A'}
              icon={AcademicCapIcon}
              bgColor="bg-gradient-to-r from-amber-500 to-orange-600"
              trend={summary.enrollmentGrowth}
            />
          </div>
        )}

        {error && summary && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-xl p-4" role="alert">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <strong className="font-bold text-red-800">Summary Error!</strong>
                <span className="block sm:inline ml-2 text-red-700">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 md:px-8 py-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                <ClockIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                <p className="text-gray-600 text-sm">Aktivitas terbaru dalam sistem</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            {activitiesLoading && (
              <div className="flex justify-center items-center py-12">
                <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-lg">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-blue-500 mr-3"></div>
                  <span className="text-blue-700">Memuat aktivitas...</span>
                </div>
              </div>
            )}
            
            {activitiesError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4" role="alert">
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <strong className="font-bold text-red-800">Error!</strong>
                    <span className="block sm:inline ml-2 text-red-700">{activitiesError}</span>
                  </div>
                </div>
              </div>
            )}
            
            {!activitiesLoading && !activitiesError && activities.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Tidak Ada Aktivitas</h3>
                <p className="text-gray-600">Belum ada aktivitas terbaru untuk ditampilkan.</p>
              </div>
            )}
            
            {!activitiesLoading && !activitiesError && activities.length > 0 && (
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {activities.map(activity => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
